import { defineStore } from 'pinia'
import { useUserStore } from './user'
import { getCtcEcoList, getHomeTickers, getLatestSymbols, queryKlineList, queryMutCandlestick } from '@/apis/home'
import { addFav, deleteFav, getCountry, getFavorite, getPrivate, getPublic } from '~/apis/base'
import { useFetch } from '#app'

export const useStore = defineStore('store', {
  state: () => ({
    publicData: { loading: true },
    privateData: {},
    contracts: [],
    currencies: [],
    spotCurrencies: [],
    contractsRaw: {},
    currenciesRaw: {},
    spotCurrenciesRaw: {},
    symbols: [],
    symbolsRaw: {},
    favContracts: new Set(),
    candlestickList: {},
    klineList: {},
    countryList: [],
    favRaw: {
      symbolIdList: [],
      contractIdList: [],
    }, // 自选列表
    tickesList: [],
    tickesListByType: {
      hot: [], // 热门榜数据
      spot: [], // 涨幅榜数据
      new: [], // 新币榜数据
      ctc: [], // CTC生态榜数据
    },
    theme: '', // 主题
    defaultTheme: 'light',
  }),
  getters: {
    isLight(s) {
      return s.theme === 'light'
    },
    isDark(s) {
      return s.theme === 'dark'
    },
    getContractIdByCode: (s) => {
      return code => s.contractsRaw[code]?.contractId
    },
    getCurrencyIdByCode: (s) => {
      return code => s.spotCurrenciesRaw[code]?.id
    },
    getCodeById: (s) => {
      return id => s.contractsRaw[id]?.code
    },
    getBaseIconByCode: (s) => {
      return code => s.contractsRaw[code]?.baseAssetIcon
    },
    getSpotIconByCode: (s) => {
      return code => s.spotCurrenciesRaw[code]?.iconAddress
    },
    getSp: (s) => {
      return code => s.spotCurrenciesRaw[code]?.sp ?? 2
    },
    baseCurrencyList: (s) => {
      return s.symbols.reduce((acc, next) => {
        if (!acc.includes(next.baseCurrency)) {
          acc.push(next.baseCurrency)
        }
        return acc
      }, [])
    },
    quoteCurrencyList: (s) => {
      return s.symbols.reduce((acc, next) => {
        if (!acc.includes(next.quoteCurrency)) {
          acc.push(next.quoteCurrency)
        }
        return acc
      }, [])
    },
    visibleSymbols() {
      return this.symbols.filter(i => i.isShow)
    },
    visibleContracts() {
      return this.contracts.filter(i => i.isShow)
    },
    getChainShowName(s) {
      return (symbol = '', chainName = '') => {
        const { chainDataList } = s.spotCurrenciesRaw[symbol.toUpperCase()] || {}
        return chainDataList?.find(i => i.chainName?.toUpperCase() === chainName.toUpperCase())?.chainShowName
      }
    },
  },
  actions: {
    changeTheme(theme) {
      this.theme = theme
      localStorage.setItem('futures-ttx-theme', theme)
      if (import.meta.client) {
        document.documentElement.setAttribute('theme', theme)
      }
    },
    async fetchPublic() {
      const { data } = await useFetch('/api/getPublic')
      if (data?.value?.success) {
        return { success: true, data: data.value.data }
      }
      else {
        return await getPublic({ spotDataState: 1 })
      }
    },
    async getOverview() {
      return null
    },
    async getPublic() {
      const { success, data } = await this.fetchPublic()

      if (success) {
        this.publicData = data
        this.contracts = this.publicData.contracts.map((i) => {
          const [,size] = i.contractSize?.toExponential().split('e') ?? ['', 0]
          const as = +size > 0 ? 0 : -size
          const name = i.name.replace('/', '')
          return {
            ...i,
            id: i.contractId,
            code: name,
            name,
            base: i.baseAsset,
            quote: i.quoteAsset,
            baseM: i.marginAsset,
            as,
            ps: i.priceScale,
            size: i.contractSize,
            riskLimits: i.riskLimits?.sort((a, b) => b.leverage - a.leverage) ?? [],
          }
        })
        this.contractsRaw = this.contracts.reduce((acc, next) => {
          acc[next.code] = next
          acc[next.id] = next
          return acc
        }, {})
        this.currencies = this.publicData.currencies.map(i => ({
          ...i,
          id: i.currencyId,
          code: i.name,
        }))

        this.currenciesRaw = this.currencies.reduce((acc, next) => {
          acc[next.code] = next
          acc[next.id] = next
          return acc
        }, {})
        this.spotCurrencies = this.publicData.spotCurrencies?.map(i => ({
          ...i,
          code: i.name,
          sp: i.showPrecision,
        })) ?? []
        this.spotCurrenciesRaw = this.spotCurrencies.reduce((acc, next) => {
          acc[next.code] = next
          acc[next.id] = next
          return acc
        }, {})
        this.symbols = (this.publicData.spotSymbols?.list.map((i) => {
          const name = i.showName.replace('/', '')
          return {
            ...i,
            id: i.instrumentId,
            code: i.symbol,
            name,
            base: i.baseCurrency,
            quote: i.quoteCurrency,
            as: i.amountPrecision,
            ps: i.pricePrecision,
          }
        }) ?? []).sort((a, b) => a.weight - b.weight)
        this.symbolsRaw = this.symbols.reduce((acc, next) => {
          acc[next.code] = next
          acc[next.id] = next
          return acc
        }, {})
      }
    },
    async getPrivate(params) {
      const { success, data } = await getPrivate(params)
      if (success)
        this.privateData = data
    },
    async loadFav() {
      const { success, data } = await getFavorite()
      if (success)
        this.favRaw = data
    },
    // 1-现货, 2-合约
    async toggleFav(id, type = 1) {
      const user = useUserStore()
      if (!user.isLogin)
        return

      const k = type === 1 ? 'symbolIdList' : 'contractIdList'
      const list = this.favRaw[k] ?? []
      const faved = list.includes(id)

      const fn = async () => {
        return faved ? deleteFav({ symbolId: id, type }) : addFav({ symbolIdList: [id], type })
      }

      const { success } = await fn({ symbolIdList: [id], type })

      if (success) {
        const o = new Set(list)

        faved ? o.delete(id) : o.add(id)

        this.favRaw[k] = Array.from(o)
      }
    },
    loadFavContracts() {
      let arr = []
      try {
        arr = JSON.parse(localStorage.getItem('favIds')).split(',')
      }
      catch (e) {}
      arr.forEach(id => this.favContracts.add(id))
    },
    toggleFavContracts(id) {
      const user = useUserStore()
      let fn = () => {}

      if (this.favContracts.has(id)) {
        fn = deleteFav
        this.favContracts.delete(id)
      }
      else {
        fn = addFav
        this.favContracts.add(id)
      }
      if (user.isLogin)
        fn({ contractId: id })
      localStorage.setItem('favIds', JSON.stringify([...this.favContracts].join(',')))
    },
    async queryMutCandlestick({ contractId, range = 3600000, limit = 24 }) {
      const { success, data } = await queryMutCandlestick({
        contractId,
        range,
        limit,
      })
      if (success) {
        this.candlestickList = data
      }
    },
    async queryKlineList({ symbol, period = '60min', size = 24 }) {
      const { success, data } = await queryKlineList({ symbol, period, size })
      if (success) {
        this.klineList = data.reduce((sofar, cur) => {
          sofar[cur.instrumentId] = cur.item.map(i => [i.startTime, i.open, i.high, i.low, i.close, i.volume])
          return sofar
        }, {})
      }
    },
    async getCountryList(sceneType = 's1') {
      const { data, success } = await getCountry({ sceneType })
      if (success) {
        this.countryList = data
      }
    },
    async  getTickers(params) {
      const { data, success } = params.tickerType === 0 ? await getLatestSymbols() : params.tickerType === 3 ? await getCtcEcoList() : await getHomeTickers(params)
      if (success) {
        this.tickesList = data
        const typeMap = {
          0: 'new', // 新币榜
          1: 'spot', // 涨幅榜
          2: 'hot', // 热门榜
          3: 'ctc', // CTC生态榜
        }
        const type = typeMap[params.tickerType]
        if (type) {
          this.tickesListByType[type] = data
        }
      }
    },
    getTickersByType(type) {
      return this.tickesListByType[type] || []
    },
  },
})

if (import.meta.client) {
  window.useStore = useStore
}
